import { OptionsViewModel, AddressViewModel } from '@models/common.models';
import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { PriceAdjustmentViewModel, ProductViewModel } from '@models/Product';
import { CoverageOption } from '@models/CoverageOption';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { RealEstateTerritoryViewModel } from '@models/RealEstateOffice';
import { EscrowOfficeViewModel } from '@models/EscrowOffice';
import { EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { CustomerViewModel } from '@models/Customer';
import { PropertyManagerViewModel } from '@models/PropertyManager';
import { DocumentViewModel } from '@models/Document';
import { ProfileViewModel } from './Auth';

export interface ContractViewModel {
  id: number;
  contractAn: string;
  assetManager: OptionsViewModel;
  assetManagerId: number;
  assetId: string;
  buyerId: number;
  contactName: string;
  contactType: OptionsViewModel;
  contactMedium: OptionsViewModel;
  contactInit: OptionsViewModel;
  coopAgentId: number;
  coopOfficeId: number;
  coopSamId?: number;
  coverageOptions: CoverageOption[];
  employeeDiscount?: PriceAdjustmentViewModel;
  escrowOfficerId: number;
  escrowId: number;
  escrowOneTimeFax: string;
  estimatedCoeDate?: Date;
  escrowComments: string;
  initAgentId?: number;
  initOfficeId: number;
  initSamId?: number;
  propertyAddress: AddressViewModel;
  propertyManagerId: number;
  propertyType: PropertyTypeViewModel;
  propertySaleType: OptionsViewModel;
  mailReturned: number;
  // initAgentSide: string;
  productGroup: OptionsViewModel;
  product: ProductViewModel;
  productChannel: OptionsViewModel;
  priceAdjustment: PriceAdjustmentViewModel;
  priceAdjustmentAmount: number;
  priceAdjustmentNotes: string;
  orderDate: Date;
  sellerCoverage: boolean;
  sellerCoverageDate: Date;
  sellerCoverageEndDate: Date;
  firstBilling: OptionsViewModel;
  laterBilling: OptionsViewModel;
  sellerId: number;
  totalPrice: number;
  txCOneTimeFax: string;
  yearBuilt?: string;
  additionalRealEstateAgentIds: Array<number>;
  transactionCoordinatorIds: Array<TransactionCoordinatorViewModel>;
  fileNumber: string;
  transCoordId?: number;
  contractEntities: Array<EntityViewModel>;
  contractNote?: string;
  contractStatus: OptionsViewModel;
  extraCharge: number;
  resaleId?: number;
  noRenew: NoRenewViewModel;
  noRenewReasons: OptionsViewModel;
}

export interface EntityViewModel {
  id: number;
  contractId: number;
  transactionCoordinatorId?: number;
  realEstateAgentId?: number;
}

export interface SimpleEntityViewModel {
  accountId: number;
  id: number;
  name: string;
  email: string;
  phone: string;
  fax: string;
}

export interface ContractSimpleViewModel {
  id: number;

  buyer: OptionsViewModel;
  contractStatus: OptionsViewModel;
  orderDate: Date;
  expirationDate: Date;
  number: string;
  propertyAddress: AddressViewModel;
  seller: OptionsViewModel;

  relationship?: string;
  resoldRelationship?: string;
}

export interface ContractDuplicateAddressReviewModel {
  contractId: number;
  contractNumber: string;
  orderDate: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  contractStatus: OptionsViewModel;
}

export interface SimpleCoverageViewModel {
  id: number;
  name: string;
  totalPrice: number;
  priceAdjustment: number;
}

export interface ContractCommentTypesViewModel {
  contractCommentTypeAn: string;
  contractCommentTypeDs: string;
  flagsQt: boolean;
}

export interface ContractDetailsViewModel extends ContractViewModel {
  contractNumber: string;
  code: string;
  estimatedCoeDate: Date;
  orderDate: Date;
  buyerId: number;
  buyer: CustomerViewModel;
  sellerId: number;
  seller: CustomerViewModel;
  propertyManagerId: number;
  propertyManager: PropertyManagerViewModel;
  initAgent: RealEstateAgentViewModel;
  initOffice: RealEstateOfficeViewModel;
  initSam: RealEstateTerritoryViewModel;
  coopAgent: RealEstateAgentViewModel;
  coopOffice: RealEstateOfficeViewModel;
  coopSam: RealEstateTerritoryViewModel;
  coopSamName: string;
  pastDueServiceFeeTotal: number;
  productId: number;
  productName: string;
  productGroupId: number;
  productGroupName: string;
  escrowOffice: EscrowOfficeViewModel;
  escrowOfficer: EscrowOfficerViewModel;
  transactionCoordinator: TransactionCoordinatorViewModel;
  documents: Array<DocumentViewModel>;
  additionalTxCoordinators: Array<SimpleEntityViewModel>;
  additionalREAgents: Array<SimpleEntityViewModel>;
  comments: Array<OptionsViewModel>;
  swOs: Array<OptionsViewModel>;
  cancelDate?: Date;
  ctcPriceAmount?: number;
  originalPrice?: number;
  paymentTotalAmount?: number;
  termEnd?: Date;
  termStart?: Date;
  verifiedDate?: Date;
  termNumber?: number;
  termPrice?: ContractPriceViewModel;
  metadata: ContractMetaDataViewModel;
  noRenew: NoRenewViewModel;
  origin: Origin;
  creator: ProfileViewModel;
  transactionCoordinators?: Array<TransactionCoordinatorViewModel>;
}

export interface PropertyTypeViewModel extends OptionsViewModel {
  over5k: boolean;
  propertyUnitsQt: number;
}

export interface ContractResaleModalViewModel {
  credit: string;
  dailyRate: number;
  daysRemaining: number;
  contractStartDate: Date;
  paidThrough: Date;
  amountPaidSoFar: number;
  contractEndDate: Date;
  contractId: number;
  contractResaleDate: Date;
  contractNumber: string;
}

export interface ContractResaleEmailViewModel {
  email: string;
  description: string;
}

export interface ContractPriceViewModel {
  contractId: number;
  termNumber: number;
  basePrice: number;
  otherFee: number;
  discount: number;
  buyerPrice: number;
  sellerPrice: number;
  totalPrice: number;
}

export interface ContractMetaDataViewModel {
  status: string;
  eligibleForActivation: boolean;
  eligibleForSWOCreation: boolean;
  eligibleForCoEExtension: boolean;
  eligibleToExtendSellers: boolean;
  eligibleForResend: boolean;
  eligibleForRenew: boolean;
  eligibleForTransfer: boolean;
  eligibleForResale: boolean;
  eligibleForTermination: boolean;
  eligibleForCancel: boolean;
  eligibleForSuspension: boolean;
  eligibleToResume: boolean;
  eligibleToVerify: boolean;
  eligibleToEdit: boolean;
}

export interface SendPaymentReceiptEmailRequest {
  creditCardLastFour?: string;
  emailAddresses: string[];
  paymentDate: Date;
}

export interface NoRenewViewModel {
  noRenewCd: number;
  noRenewDate: Date | null;
  noRenewDs?: string;
}

export interface EmailNotificationViewModel {
  email: string;
  description: string;
}

export interface ContractEmailForm {
  contractEmailList: Array<EmailNotificationViewModel>;
}

export interface ContractSendEmailsRequest {
  emailNotifications: Array<EmailNotificationViewModel>;
  noDemandLetter: boolean;
  useDefaults: boolean;
}

export interface ContractBalancesViewModel {
  contractId: number;
  balanceType: OptionsViewModel;
  entityId: number;
  amountDue: number;
  paymentDate?: Date;
}

export enum ContractOrderType {
  DTC = 'dtc',
  RealtyEscrow = 'realtyEscrow',
  Invalid = 'invalid'
}

export enum Origin {
  WISE = 2,
  Web = 4
}

export interface ContractContractModel {
  contractId: number;
  relatedContractNum: string;
  relationship: string;
}



// WEBPACK FOOTER //
// ./src/_models/Contract.ts